import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import { env } from '@/env';
import { isNative, tokenSync } from '@/lib/native-auth';
import {
  visitaBundleSchema,
  visitaDetailSchema,
  visitaListSchema,
  syncResponseSchema,
  type SyncResponse,
  type VisitaBundle,
} from './schemas';
import {
  getBundle,
  saveBundle,
  getVisita,
  upsertVisita,
  fotosDaVisita,
  updateFoto,
  amostrasDaVisita,
  updateAmostra,
  visitasPendentes,
  type PendingAudio,
  type PendingFoto,
} from './offline';

// Visitas finalizadas e ainda não sincronizadas (banner "Sincronizar").
export function usePendingVisitas(imovelIds: number[] | null) {
  return useQuery({
    queryKey: ['visita', 'pendentes', [...(imovelIds ?? [])].sort((a, b) => a - b)],
    queryFn: () => visitasPendentes(imovelIds ?? undefined),
    enabled: imovelIds != null,
    staleTime: 2000,
  });
}

const keys = {
  bundle: (id: number | string) => ['agronomia', 'visita-bundle', String(id)] as const,
  visita: (id: number | string) => ['agronomia', 'visita', String(id)] as const,
  visitas: (id: number | string) => ['agronomia', 'visitas', String(id)] as const,
};

// Bundle da fazenda: busca online e cacheia em IndexedDB; offline usa o cache.
export function useVisitaBundle(imovelId: number | null) {
  return useQuery({
    queryKey: keys.bundle(imovelId ?? ''),
    enabled: imovelId != null,
    staleTime: 60_000,
    // Roda mesmo offline (senão o React Query pausa a query e o queryFn — que tem o
    // fallback pro cache — nunca executa, deixando resolverTalhao sem talhões).
    networkMode: 'always',
    queryFn: async ({ signal }) => {
      try {
        const b = await api(`/api/v2/imoveis/${imovelId}/visita-bundle`, {
          schema: visitaBundleSchema,
          signal,
        });
        await saveBundle(imovelId as number, b);
        return b;
      } catch (e) {
        const cached = await getBundle(imovelId as number);
        if (cached) return cached;
        throw e;
      }
    },
  });
}

// Visita multi-fazenda (feita pela tela do produtor): junta os bundles das
// fazendas escolhidas num só, pra o GPS resolver o talhão em qualquer uma delas.
export function useVisitaBundles(imovelIds: number[]) {
  const ids = [...imovelIds].sort((a, b) => a - b);
  return useQuery({
    queryKey: ['agronomia', 'visita-bundles', ids],
    enabled: ids.length > 0,
    staleTime: 60_000,
    networkMode: 'always',
    queryFn: async ({ signal }) => {
      const bundles: VisitaBundle[] = [];
      for (const id of ids) {
        try {
          const b = await api(`/api/v2/imoveis/${id}/visita-bundle`, { schema: visitaBundleSchema, signal });
          await saveBundle(id, b);
          bundles.push(b);
        } catch (e) {
          const cached = await getBundle(id);
          if (cached) bundles.push(cached);
          else if (ids.length === 1) throw e;
        }
      }
      return {
        fazenda: bundles[0]?.fazenda ?? null,
        fazendas: bundles.map((b) => b.fazenda).filter((f): f is NonNullable<typeof f> => f != null),
        // O talhão carrega o imovel_id; quando o backend não mandou, herda da fazenda do bundle.
        talhoes: bundles.flatMap((b) =>
          b.talhoes.map((t) => ({ ...t, imovel_id: t.imovel_id ?? b.fazenda?.imovel_id ?? null }))),
        catalogo: bundles.find((b) => b.catalogo)?.catalogo,
        gerado_em: new Date().toISOString(),
      };
    },
  });
}

export function useVisita(id: number | null) {
  return useQuery({
    queryKey: keys.visita(id ?? ''),
    enabled: id != null,
    staleTime: 30_000,
    queryFn: ({ signal }) => api(`/api/v2/visitas/${id}`, { schema: visitaDetailSchema, signal }),
  });
}

export function useVisitasFazenda(imovelId: number | null) {
  return useQuery({
    queryKey: keys.visitas(imovelId ?? ''),
    enabled: imovelId != null,
    staleTime: 30_000,
    queryFn: ({ signal }) =>
      api(`/api/v2/imoveis/${imovelId}/visitas`, { schema: visitaListSchema, signal }),
  });
}

export function visitaRelatorioPdfUrl(id: number): string {
  if (isNative()) {
    const tk = tokenSync();
    return `${env.VITE_API_BASE}/api/v2/visitas/${id}/relatorio.pdf${tk ? `?token=${encodeURIComponent(tk)}` : ''}`;
  }
  return `/api/v2/visitas/${id}/relatorio.pdf`;
}

// Link web do relatório de monitoramento (HTML estilizado, compartilhável).
export function visitaRelatorioUrl(id: number): string {
  if (isNative()) {
    const tk = tokenSync();
    return `${env.VITE_API_BASE}/api/v2/visitas/${id}/relatorio${tk ? `?token=${encodeURIComponent(tk)}` : ''}`;
  }
  return `/api/v2/visitas/${id}/relatorio`;
}

// URL do mapa Leaflet da sessão com pontos de amostragem (iframe). Reusa o mapa
// da fazenda; os pontos vêm plotados pelo HTML do servidor (camada amostras).
export function visitaMapaUrl(imovelId: number, visitaId: number): string {
  const base = isNative() ? env.VITE_API_BASE : '';
  const sp = new URLSearchParams({ visita: String(visitaId) });
  if (isNative()) { const tk = tokenSync(); if (tk) sp.set('token', tk); }
  return `${base}/api/v2/imoveis/${imovelId}/talhoes/mapa?${sp.toString()}`;
}

// Timeout p/ chamadas de IA (que podem demorar) — vira AbortError no api(), que
// a UI exibe (em vez de spinner infinito no app).
function timeoutOpt(ms: number): { signal: AbortSignal } | Record<string, never> {
  try { return { signal: AbortSignal.timeout(ms) }; } catch { return {}; }
}

// Gera/atualiza o resumo executivo por IA (Jão) de uma sessão de monitoramento.
const resumoIaResponseSchema = z.object({ ok: z.boolean(), resumo: z.string() });
export function useResumoIA(visitaId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/api/v2/visitas/${visitaId}/resumo-ia`, { method: 'POST', schema: resumoIaResponseSchema, ...timeoutOpt(90000) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.visita(visitaId) }),
  });
}

// Salva o resumo/diagnóstico EDITADO pelo agrônomo (revisão do texto da IA).
export function useSaveResumo(visitaId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (resumo_ia: string) => api(`/api/v2/visitas/${visitaId}`, { method: 'PATCH', body: { resumo_ia } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.visita(visitaId) }),
  });
}

// Áudios de uma foto na fila offline. Cai no áudio único das versões antigas do
// app (que gravavam em audioBlob) pra visita pendente no aparelho não se perder
// depois de atualizar o APK.
function audiosDaFoto(f: PendingFoto): PendingAudio[] {
  if (f.audios && f.audios.length) return f.audios;
  if (f.audioBlob) {
    return [{
      clientUuid: f.clientUuid,
      blob: f.audioBlob,
      mime: f.audioMime || 'audio/webm',
      filename: f.audioFilename || 'recomendacao.webm',
      durMs: f.audioDurMs ?? null,
    }];
  }
  return [];
}

// Drena a fila offline de uma sessão num POST multipart idempotente.
export async function syncVisita(visitaUuid: string): Promise<SyncResponse> {
  const visita = await getVisita(visitaUuid);
  if (!visita) throw new Error('Visita não encontrada na fila offline');
  const fotos = await fotosDaVisita(visitaUuid);
  const pendentes = fotos.filter((f) => !f.synced);
  const amostras = (await amostrasDaVisita(visitaUuid)).filter((a) => !a.synced);

  const payload = {
    client_uuid: visita.clientUuid,
    imovel_id: visita.imovelId,
    imovel_ids: visita.imovelIds && visita.imovelIds.length ? visita.imovelIds : undefined,
    agronomo_nome: visita.agronomoNome,
    data_visita: visita.dataVisita,
    observacoes_gerais: visita.observacoes,
    device_info: { plataforma: isNative() ? 'android' : 'web' },
    fotos: pendentes.map((f) => ({
      client_uuid: f.clientUuid,
      // ponto a que a foto pertence (agrupa galeria por GPS); vincula a amostra.
      ponto_uuid: f.pontoUuid ?? null,
      // ponto marcado sem foto: o servidor cria a linha do ponto sem arquivo.
      sem_foto: f.semFoto === true && !f.blob,
      lat: f.lat,
      lon: f.lon,
      capturado_em: f.capturadoEm,
      recomendacao: f.recomendacao || null,
      estadio_override: null,
      // N áudios por foto; o arquivo de cada um vai como audio_<client_uuid>.
      audios: audiosDaFoto(f).map((a, i) => ({ client_uuid: a.clientUuid, dur_ms: a.durMs, ordem: i })),
      // Ocorrências apontadas a campo (praga + quantidade contada no ponto).
      ocorrencias: (f.ocorrencias ?? []).map((o) => ({
        client_uuid: o.clientUuid,
        tipo: o.tipo,
        nome: o.nome,
        quantidade: o.quantidade,
        unidade: o.unidade,
        severidade: o.severidade,
      })),
    })),
    // Pontos da visita (agrupam N fotos por GPS). observacao = recomendação do
    // ponto; áudio do ponto vai como audio_<client_uuid>. medicoes = MIP, se houver.
    amostras: amostras.map((a) => ({
      client_uuid: a.clientUuid,
      lat: a.lat,
      lon: a.lon,
      capturado_em: a.capturadoEm,
      observacao: a.observacao || null,
      talhao_id: a.talhaoId ?? null,
      estadio: a.estadio ?? null,
      foto_client_uuid: a.fotoClientUuid || null,
      audio_dur_ms: a.audioDurMs ?? null,
      medicoes: (a.medicoes || []).map((m) => ({
        mip_item_id: m.mip_item_id,
        item_nome: m.item_nome,
        fase: m.fase,
        valor: m.valor,
        unidade: m.unidade,
      })),
    })),
  };

  const fd = new FormData();
  fd.append('payload', JSON.stringify(payload));
  for (const f of pendentes) {
    if (f.blob) fd.append('foto_' + f.clientUuid, f.blob, f.filename);
    for (const a of audiosDaFoto(f)) fd.append('audio_' + a.clientUuid, a.blob, a.filename);
  }
  // Áudio do PONTO (recomendação do agrônomo por ponto).
  for (const a of amostras) {
    if (a.audioBlob) fd.append('audio_' + a.clientUuid, a.audioBlob, a.audioFilename || 'recomendacao.webm');
  }

  const res = await api('/api/v2/visitas', { method: 'POST', body: fd, schema: syncResponseSchema });

  for (const f of pendentes) await updateFoto(f.clientUuid, { synced: true });
  for (const a of amostras) await updateAmostra(a.clientUuid, { synced: true });
  await upsertVisita({
    ...visita,
    status: 'sincronizada',
    syncedVisitaId: res.visita_id ?? visita.syncedVisitaId,
  });

  // Após sincronizar, a IA transcreve os áudios (não quebra o sync se falhar).
  if (res.visita_id && pendentes.some((f) => audiosDaFoto(f).length > 0)) {
    try { await api(`/api/v2/visitas/${res.visita_id}/transcrever`, { method: 'POST' }); } catch { /* segue sem transcrição */ }
  }
  return res;
}

export function useSyncVisita() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (visitaUuid: string) => syncVisita(visitaUuid),
    onSuccess: (res) => {
      // Atualiza histórico do talhão e listas após o sync.
      void qc.invalidateQueries({ queryKey: ['agronomia', 'relatorios'] });
      void qc.invalidateQueries({ queryKey: ['agronomia', 'visitas'] });
      if (res.visita_id) void qc.invalidateQueries({ queryKey: ['agronomia', 'visita', String(res.visita_id)] });
    },
  });
}

// ── Fase 2: IA de visão + revisão ────────────────────────────────────────
const analisarResponseSchema = z.object({
  ok: z.boolean(),
  total: z.coerce.number(),
  analisadas: z.coerce.number(),
  erros: z.coerce.number(),
});

export function useAnalisarVisita(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/api/v2/visitas/${id}/analisar`, { method: 'POST', schema: analisarResponseSchema, ...timeoutOpt(240000) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.visita(id) }),
  });
}

// Reanálise de UMA foto (corrige/atualiza com o prompt recalibrado).
export function useReanalisarFoto(visitaId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fotoId: number) => api(`/api/v2/visitas/fotos/${fotoId}/analisar`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.visita(visitaId) }),
  });
}

export function usePatchFoto(visitaId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fotoId, body }: { fotoId: number; body: { recomendacao?: string; ia_status?: string; recomendacao_audio_transcricao?: string; legenda?: string; ia_analise?: unknown } }) =>
      api(`/api/v2/visitas/fotos/${fotoId}`, { method: 'PATCH', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.visita(visitaId) }),
  });
}

// Transcrição de UM áudio, revisada pelo agrônomo.
export function usePatchAudio(visitaId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ audioId, transcricao }: { audioId: number; transcricao: string }) =>
      api(`/api/v2/visitas/foto-audios/${audioId}`, { method: 'PATCH', body: { transcricao } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.visita(visitaId) }),
  });
}

export interface OcorrenciaInput {
  tipo: string;
  nome?: string | null;
  severidade?: string | null;
  quantidade?: number | null;
  unidade?: string | null;
  observacao?: string | null;
  // Ponto de onde veio o apontamento (contagem a campo) — o PUT reescreve a
  // lista inteira, então precisa devolver isso pra não perder a origem.
  ponto_uuid?: string | null;
}

export function usePutOcorrencias(visitaId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ relatorioId, ocorrencias }: { relatorioId: number; ocorrencias: OcorrenciaInput[] }) =>
      api(`/api/v2/visitas/relatorios/${relatorioId}/ocorrencias`, { method: 'PUT', body: { ocorrencias } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.visita(visitaId) });
      void qc.invalidateQueries({ queryKey: ['agronomia', 'relatorios'] });
    },
  });
}

export function useFinalizarVisita(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/api/v2/visitas/${id}`, { method: 'PATCH', body: { status: 'revisado' } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.visita(id) });
      // o relatório "nasce" — atualiza badge de revisão + calendário do consolidado.
      void qc.invalidateQueries({ queryKey: ['agronomia', 'visitas'] });
      void qc.invalidateQueries({ queryKey: ['agronomia', 'datas-visita'] });
    },
  });
}
